import React, { useEffect, useState, useRef } from "react";
import { Button } from "@cdk-uip/react-button";
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  Chip,
  RadioGroup,
  Card,
  INPUT_SIZES,
  IconFileUpload,
  Loader,
  Dialog,
  BUTTON_VARIANTS,
} from "cdk-radial";
import config from "../config/app.conf.json";
import axios from "axios";
import withNavigationToBundles from "./withNavigationToBundles";
import "./style.css";
import BundleDocsList from "./BundleDocsList";
import { BUNDLE_DOC_TYPES } from "../Utils/constants";

const options = [
  {
    id: "radio-bundle-details",
    label: "Bundle Details",
    value: "external",
  },
  {
    id: "radio-bundle-terms",
    label: "Terms and Conditions",
    value: "terms",
  },
];

function BundleDocs({
  auth,
  setScreen,
  setSelectedBundle,
  setShowSuccessToast,
  setToastMessage,
  ApiBundleManagementReducer,
}) {
  const [showOverviewInput, setShowOverviewInput] = useState(false);
  const { selectedBundle } = ApiBundleManagementReducer;
  const [errorFetchingApis, setErrorFetchingApis] = useState(false);
  const [errorMessageFetchingApis, setErrorMessageFetchingApis] = useState("");
  const [resourceName, setResourceName] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [docUpdate, setDocUpdate] = useState(false);
  // doc map
  const [bundleDocNameMap, setBundleDocNameMap] = useState({});
  // terms doc
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [activeDocId, setActiveDocId] = useState(null);
  // external docs
  const [activateDocIds, setActivateDocIds] = useState([]);
  const [deactivateDocIds, setDeactivateDocIds] = useState([]);
  const [initialExternalDocIds, setInitialExternalDocIds] = useState({
    active: [],
    inactive: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [termsDocumentList, setTermsDocumentList] = useState([]);
  const [externalDocumentList, setExternalDocumentList] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [resourceType, setResourceType] = useState(BUNDLE_DOC_TYPES.EXTERNAL);

  const { accessToken } = auth;
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    setResourceName(event.target.value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const onChangeResourceType = (event) => {
    setResourceType(event.target.value);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getBreadcrumbs = () => {
    if (showOverviewInput) {
      return [
        {
          label: "Bundle Management",
          onClick: () => setScreen("View Bundles"),
        },
        {
          label: "Documents",
          onClick: () => {
            setShowOverviewInput(false);
          },
        },
        { label: "Add a Document" },
      ];
    } else {
      return [
        {
          label: "Bundle Management",
          onClick: () => setScreen("View Bundles"),
        },
        { label: "Documents" },
      ];
    }
  };

  const parseErrorMessage = (error) => {
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    } else if (error.message) {
      return error.message;
    } else {
      return "An unexpected error occurred";
    }
  };

  const handleDocsSave = async () => {
    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append("resourceFile", selectedFile);
      formData.append("resourceName", resourceName);
      formData.append("resourceType", resourceType);
      formData.append("bundleId", selectedBundle.id);

      await axios.post(
        `${config.api.api_gateway_url}/v1/admin/bundle/terms`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setUploadingFile(false);
      await fetchBundleDocs();
      setShowOverviewInput(false);
      setResourceName("");
      setSelectedFile(null);
    } catch (error) {
      const parsedError = parseErrorMessage(
        error,
        `Failed to save documentation`,
      );
      setUploadError(parsedError);
      setShowErrorDialog(true);
    } finally {
      setUploadingFile(false);
    }
  };

  const termsDocUpdate = (docId) => {
    return axios.patch(
      `${config.api.api_gateway_url}/v1/admin/bundles/${selectedBundle.id}/documents/${docId}`,
      { status: "active" },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  };

  const detailsDocUpdate = (activateDocIds, deactivateDocIds) => {
    return axios.patch(
      `${config.api.api_gateway_url}/v1/admin/bundles/${selectedBundle.id}/documents/details`,
      { active: activateDocIds, inactive: deactivateDocIds, retired: [] },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  };

  const deleteDetailsDoc = (retireDocId) => {
    return axios.patch(
      `${config.api.api_gateway_url}/v1/admin/bundles/${selectedBundle.id}/documents/details`,
      { active: [], inactive: [], retired: [retireDocId] },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  };

  const handleTermsDocUpdate = async (
    docId,
    activateDocIds,
    deactivateDocIds,
  ) => {
    const docName = [];
    const UpdatePromises = [];
    try {
      setDocUpdate(true);
      if (docId !== activeDocId) {
        docName.push(bundleDocNameMap[docId]);
        UpdatePromises.push(termsDocUpdate(docId));
      }
      if (activateDocIds.length > 0 || deactivateDocIds.length > 0) {
        [...activateDocIds, ...deactivateDocIds].forEach((id) =>
          docName.push(bundleDocNameMap[id]),
        );
        UpdatePromises.push(detailsDocUpdate(activateDocIds, deactivateDocIds));
      }
      await Promise.all(UpdatePromises);
      setShowSuccessToast(true);
      const message = (
        <div>
          Document <strong>{docName.join(", ")}</strong> is saved successfully
          for bundle <strong>{selectedBundle?.name}</strong>
        </div>
      );
      setToastMessage(message);
      setDocUpdate(false);
      setActiveDocId(docId);
      setSelectedDocId(docId);
      setScreen("View Bundles");
    } catch (error) {
      const parsedError = parseErrorMessage(
        error,
        `Failed to update documentation`,
      );
      setDocUpdate(false);
      setUploadError(parsedError);
      setShowErrorDialog(true);
    }
  };

  const fetchBundleDocs = async () => {
    setIsLoading(true);
    try {
      if (!selectedBundle?.id) {
        throw new Error("Bundle ID is missing.");
      }

      const response = await axios.get(
        `${config.api.api_gateway_url}/v1/admin/bundle/${selectedBundle.id}/documents`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const termsDoc =
        response?.data && response?.data != "" && response?.data.length > 0
          ? response?.data?.filter(
              (doc) =>
                doc.docType === BUNDLE_DOC_TYPES.TERMS &&
                doc.status != "retired",
            )
          : [];
      const externalDoc =
        response?.data && response?.data != "" && response?.data.length > 0
          ? response?.data?.filter(
              (doc) =>
                doc.docType === BUNDLE_DOC_TYPES.EXTERNAL &&
                doc.status != "retired",
            )
          : [];

      const bundleDocMap = {};
      [...termsDoc, ...externalDoc].forEach((doc) => {
        bundleDocMap[doc.id] = doc.docName;
      });
      setBundleDocNameMap(bundleDocMap);
      setTermsDocumentList(termsDoc);
      setExternalDocumentList(externalDoc);
      setIsLoading(false);
    } catch (error) {
      setErrorFetchingApis(true);
      const parsedError = parseErrorMessage(error);
      setErrorMessageFetchingApis(parsedError);
      setShowErrorDialog(true);
      setIsLoading(false);
    }
  };

  const disableSaveButton = () => {
    return (
      docUpdate ||
      (activeDocId === selectedDocId &&
        activateDocIds.length === 0 &&
        deactivateDocIds.length === 0)
    );
  };

  useEffect(() => {
    fetchBundleDocs();
  }, [selectedBundle?.id, accessToken]);

  return (
    <>
      {showErrorDialog && (uploadError || errorMessageFetchingApis) ? (
        <Dialog
          className="error-details-dialog"
          buttonsProps={[
            {
              id: "error-ok-action",
              onClick: () => {
                setShowErrorDialog(false);
                setErrorMessageFetchingApis("");
                setUploadError("");
              },
              text: "Cancel",
              variant: BUTTON_VARIANTS.SECONDARY,
            },
          ]}
          id="error-details-dialog"
          isOpen={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorMessageFetchingApis("");
            setUploadError("");
          }}
          title="Bundle Document Failed"
        >
          <div className="error-details">
            <div className="error-message">
              {uploadError || errorMessageFetchingApis}
            </div>
          </div>
        </Dialog>
      ) : null}
      {errorFetchingApis ? (
        <div className="error-div">Something went wrong, please try again.</div>
      ) : isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Loader
            className="bundle-docs-loader"
            data-testid="bundle_docs_loader"
            label="Loading documents..."
            variant="inline"
          />
        </div>
      ) : (
        <Card>
          <div className="create-bundle-card">
            <div className="bundle-pricing-title">{selectedBundle?.name}</div>
            <div>
              {getBreadcrumbs().map((crumb, idx) => {
                const isAddPlanScreen = showOverviewInput;
                const isBundlePricingScreen = !showOverviewInput;
                const isFirstCrumb = idx === 0;
                let textDecoration = "none";
                if (
                  crumb.onClick &&
                  !isAddPlanScreen &&
                  !(isBundlePricingScreen && isFirstCrumb)
                ) {
                  textDecoration = "underline";
                }
                return (
                  <span
                    key={idx}
                    className={`bundle-pricing-breadcrumb${
                      crumb.onClick ? " clickable" : ""
                    }`}
                    onClick={crumb.onClick}
                    style={{
                      cursor: crumb.onClick ? "pointer" : "default",
                      color: crumb.onClick ? "#5E2368" : "inherit",
                      textDecoration,
                      fontWeight: crumb.onClick ? "600" : "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: "0px",
                      fontStyle: "normal",
                    }}
                  >
                    {crumb.label}
                    {idx < getBreadcrumbs().length - 1 && " > "}
                  </span>
                );
              })}
            </div>

            <div className="upload-doc-header">
              <div>
                <div className="bundle-pricing-text">
                  {showOverviewInput
                    ? "Upload Document"
                    : "Bundle Documentation"}
                </div>
                <div></div>
                <div className="bundle_pricing_description">
                  {showOverviewInput
                    ? "Upload a new document into this bundle."
                    : "Use this section to manage your bundle documentation."}
                </div>
              </div>
              {!showOverviewInput && (
                <SecondaryButton
                  dataTestId="upload-bundle-doc"
                  icon={<IconFileUpload />}
                  text="Upload Document"
                  onClick={() => setShowOverviewInput(true)}
                />
              )}
            </div>
            <div className="api-details-panel-save-button">
              {showOverviewInput ? (
                <div>
                  {uploadingFile ? (
                    <Loader
                      label={
                        resourceType === BUNDLE_DOC_TYPES.EXTERNAL
                          ? "Uploading bundle details document..."
                          : "Uploading bundle terms document..."
                      }
                    />
                  ) : (
                    <>
                      <div css="max-width: 224px;" style={{ width: "400px" }}>
                        <div
                          style={{ marginBottom: "24px", marginLeft: "-12px" }}
                        >
                          <RadioGroup
                            dataTestId="bundle-doc-group"
                            id="bundle-doc-group"
                            name="bundle-doc-group"
                            onChange={onChangeResourceType}
                            options={options}
                            value={resourceType}
                          />
                        </div>
                        <Input
                          id="input-simple"
                          style={{ width: "282px" }}
                          label="Document Name"
                          name="input-simple"
                          placeholder="Enter Document Name"
                          size={INPUT_SIZES.LARGE}
                          maxLength={100}
                          isRequired
                          onChange={handleChange}
                          onClick={handleChange}
                          value={resourceName}
                        />
                      </div>

                      <div
                        style={{
                          marginTop: "0px",
                          maxWidth: "224px",
                          height: "fit-content",
                          minHeight: "81.75px",
                        }}
                        className="file-selection-input"
                      >
                        <div>
                          <input
                            style={{ display: "none" }}
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={handleFileChange}
                          />
                          <div style={{ width: "120px", marginTop: "24px" }}>
                            <SecondaryButton
                              icon={<IconFileUpload />}
                              text="Add File"
                              onClick={handleChooseFileClick}
                            />
                          </div>
                          {selectedFile && (
                            <div className="file-name-chip">
                              <Chip
                                isSelected={true}
                                label={
                                  selectedFile
                                    ? selectedFile.name
                                    : "File name chip"
                                }
                                onRemove={handleRemoveFile}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div
                    className="bundle_card-edit-button"
                    style={{ width: "282px", marginTop: "24px" }}
                  >
                    <PrimaryButton
                      dataTestId="build_with_bundle"
                      iconColor="primary"
                      text={uploadingFile ? "Saving..." : "Save"}
                      onClick={handleDocsSave}
                      isDisabled={
                        uploadingFile || !resourceName.trim() || !selectedFile
                      }
                      style={{ marginRight: "16px" }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {!termsDocumentList || termsDocumentList.length === 0 ? (
                    <div className="bundle_pricing_content">
                      You do not have any bundle terms documents.
                    </div>
                  ) : (
                    <BundleDocsList
                      bundleDocType={BUNDLE_DOC_TYPES.TERMS}
                      bundleDocs={termsDocumentList}
                      bundleId={selectedBundle?.id}
                      accessToken={accessToken}
                      selectedDocId={selectedDocId}
                      setSelectedDocId={setSelectedDocId}
                      activeDocId={activeDocId}
                      setActiveDocId={setActiveDocId}
                    />
                  )}

                  {!externalDocumentList ||
                  externalDocumentList.length === 0 ? (
                    <div className="bundle_pricing_content">
                      You do not have any bundle details documents.
                    </div>
                  ) : (
                    <BundleDocsList
                      bundleDocType={BUNDLE_DOC_TYPES.EXTERNAL}
                      bundleDocs={externalDocumentList}
                      bundleId={selectedBundle?.id}
                      accessToken={accessToken}
                      setActivateDocIds={setActivateDocIds}
                      setDeactivateDocIds={setDeactivateDocIds}
                      initialExternalDocIds={initialExternalDocIds}
                      setInitialExternalDocIds={setInitialExternalDocIds}
                      setExternalDocumentList={setExternalDocumentList}
                      deleteDetailsDoc={deleteDetailsDoc}
                      setUploadError={setUploadError}
                      setShowErrorDialog={setShowErrorDialog}
                    />
                  )}
                  <div className="bundle-pricing-buttons">
                    {(termsDocumentList.length > 0 || externalDocumentList.length > 0) && (
                      <PrimaryButton
                        dataTestId="save-bundle"
                        text={docUpdate ? "Saving..." : "Save"}
                        icon={docUpdate ? <Loader /> : <></>}
                        isIconTrailing={false}
                        isDisabled={disableSaveButton()}
                        onClick={() =>
                          handleTermsDocUpdate(
                            selectedDocId,
                            activateDocIds,
                            deactivateDocIds,
                          )
                        }
                        style={{ marginRight: "8px" }}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

export default withNavigationToBundles(BundleDocs);
